import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, DollarSign, Target, Tag, Globe, FileText, Pencil, Trash2, HardDrive, ExternalLink, Download, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { authFetch, downloadFile } from '../lib/api';

const CHANNEL_LABELS = {
  Paid_Search: 'Paid Search',
  Paid_Social: 'Paid Social',
  Programmatic: 'Programmatic',
  Influencer: 'Influencers',
  SEO: 'SEO',
};

const BriefDetail = ({ user }) => {
  const { briefId } = useParams();
  const navigate = useNavigate();
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');
  const [landingPageInput, setLandingPageInput] = useState('');

  useEffect(() => {
    const loadBrief = async () => {
      try {
        
        const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/briefs/${briefId}`);
        if (res.ok) {
          const data = await res.json();
          setBrief(data.brief);
        } else {
          toast.error('Brief not found');
          navigate('/internal/workflows');
        }
      } catch (e) { toast.error('Failed to load brief'); }
      finally { setLoading(false); }
    };
    loadBrief();
  }, [briefId, navigate]);

  const fetchBrief = async () => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/briefs/${briefId}`);
      if (res.ok) {
        const data = await res.json();
        setBrief(data.brief);
      } else {
        toast.error('Brief not found');
        navigate('/internal/workflows');
      }
    } catch (e) { toast.error('Failed to load brief'); }
    finally { setLoading(false); }
  };

  const startEdit = () => {
    setEditData({
      objective: brief.objective || '',
      budget_total: brief.budget_total || '',
      budget_daily: brief.budget_daily || '',
      start_date: brief.start_date || '',
      end_date: brief.end_date || '',
      industry_vertical: brief.industry_vertical || '',
      platforms: brief.platforms || [],
      ad_products: brief.ad_products || [],
      mandatories: brief.mandatories || { required_keywords: [] },
      landing_pages: brief.landing_pages || [],
      additional_notes: brief.additional_notes || '',
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    try {
      const clean = { ...editData };
      if (!clean.budget_total && clean.budget_total !== 0) clean.budget_total = null;
      if (!clean.budget_daily && clean.budget_daily !== 0) clean.budget_daily = null;
      if (!clean.end_date) clean.end_date = null;

      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/briefs/${briefId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(clean)
      });
      if (res.ok) {
        const data = await res.json();
        setBrief(data.brief);
        setEditing(false);
        toast.success('Brief updated');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Update failed');
      }
    } catch (e) { toast.error('Network error'); }
  };

  const handleDelete = async () => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/briefs/${briefId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success('Brief deleted');
        navigate('/internal/workflows');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Delete failed');
      }
    } catch (e) { toast.error('Network error'); }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setGenProgress({ phase: 'starting', detail: 'Initiating campaign generation...', percent: 0 });
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/briefs/${briefId}/generate-campaigns`, {
        method: 'POST'
      });
      if (res.ok) {
        setBrief(prev => ({ ...prev, status: 'In_Progress' }));
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Generation failed — try again shortly.');
        setGenerating(false);
        setGenProgress(null);
      }
    } catch (e) {
      toast.error('Network error starting generation');
      setGenerating(false);
      setGenProgress(null);
    }
  };

  // Auto-poll whenever brief is In_Progress (works on mount, generate, and navigate-back)
  useEffect(() => {
    if (!brief || brief.status !== 'In_Progress') return;

    setGenerating(true);
    if (!genProgress) {
      setGenProgress(brief.generation_progress || { phase: 'resuming', detail: 'Resuming progress...', percent: 10 });
    }

    let cancelled = false;
    const poll = async () => {
      while (!cancelled) {
        await new Promise(r => setTimeout(r, 2500));
        if (cancelled) break;
        try {
          const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/briefs/${briefId}`);
          if (!res.ok) continue;
          const data = await res.json();

          if (data.brief.generation_progress) {
            setGenProgress(data.brief.generation_progress);
          }

          if (data.brief.status === 'Completed') {
            setBrief(data.brief);
            setGenProgress({ phase: 'done', detail: 'Complete', percent: 100 });
            setGenerating(false);
            toast.success('Campaign structure generated!');
            return;
          }

          if (data.brief.status === 'Draft' && data.brief.generation_error) {
            setBrief(data.brief);
            setGenProgress(null);
            setGenerating(false);
            toast.error(`Generation failed: ${data.brief.generation_error}`);
            return;
          }
        } catch (e) { console.warn('Poll error:', e.message); }
      }
    };

    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brief?.status, briefId]);

  const handleSaveToDrive = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/briefs/${briefId}/save-to-drive`, {
        method: 'POST'
      });
      if (res.ok) {
        const data = await res.json();
        setBrief(prev => ({ ...prev, drive_files: data.files }));
        toast.success('Brief saved to Google Drive!');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save to Drive');
      }
    } catch (e) { toast.error('Network error'); }
    finally { setSaving(false); }
  };

  const handleDownloadPdf = async () => {
    try {
      toast.info('Generating PDF...');
      await downloadFile(
        `/api/briefs/${briefId}/export-pdf`,
        `BCM_${(brief.channel || 'Campaign').replace('_', ' ')}_Brief_${briefId.slice(0, 8)}.pdf`
      );
      toast.success('PDF downloaded');
    } catch (e) { toast.error('Network error'); }
  };

  const handlePushToSkai = async () => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/briefs/${briefId}/push-to-skai`, {
        method: 'POST'
      });
      if (res.ok) { toast.success('Campaigns pushed to Skai!'); }
      else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to push to Skai');
      }
    } catch (e) { toast.error('Network error'); }
  };

  const addEditKeyword = () => {
    if (keywordInput.trim()) {
      setEditData(prev => ({
        ...prev,
        mandatories: {
          ...prev.mandatories,
          required_keywords: [...(prev.mandatories?.required_keywords || []), keywordInput.trim()]
        }
      }));
      setKeywordInput('');
    }
  };

  const removeEditKeyword = (idx) => {
    setEditData(prev => ({
      ...prev,
      mandatories: {
        ...prev.mandatories,
        required_keywords: prev.mandatories.required_keywords.filter((_, i) => i !== idx)
      }
    }));
  };

  const addEditLandingPage = () => {
    if (landingPageInput.trim()) {
      setEditData(prev => ({ ...prev, landing_pages: [...prev.landing_pages, landingPageInput.trim()] }));
      setLandingPageInput('');
    }
  };

  const removeEditLandingPage = (idx) => {
    setEditData(prev => ({ ...prev, landing_pages: prev.landing_pages.filter((_, i) => i !== idx) }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-page flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
      </div>
    );
  }
  if (!brief) return null;

  const statusColor = brief.status === 'Completed'
    ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
    : brief.status === 'In_Progress'
    ? 'bg-blue-100 text-blue-800 border-blue-200'
    : 'bg-amber-100 text-amber-800 border-amber-200';

  const canEdit = user.role === 'Admin' || user.role === 'Media_Buyer' || brief.created_by === user.id;
  const canDelete = user.role === 'Admin' || brief.created_by === user.id;

  return (
    <div className="min-h-screen bg-surface-page p-6 md:p-8 lg:p-12" data-testid="brief-detail">
      <div className="max-w-5xl mx-auto">
        <button onClick={() => navigate('/internal/workflows')} className="flex items-center gap-2 text-brand hover:text-text-primary mb-6" data-testid="back-to-workflows">
          <ArrowLeft size={20} /> Back to Workflows
        </button>

        {/* Header */}
        <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-semibold tracking-tight" style={{ color: '#1d585f' }}>
                {brief.objective} — {CHANNEL_LABELS[brief.channel] || brief.channel}
              </h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${statusColor}`}>
                {brief.status?.replace('_', ' ')}
              </span>
            </div>
            <p className="text-text-tertiary text-sm">
              Brief ID: {brief.id} | Created: {brief.created_at ? new Date(brief.created_at).toLocaleString() : 'N/A'}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => {
                const url = `${window.location.origin}/internal/briefs/${brief.id}`;
                const channelLabel = CHANNEL_LABELS[brief.channel] || brief.channel;
                const prefill = `Sharing this brief — take a look:\n*${brief.objective} — ${channelLabel}*\n${url}`;
                window.dispatchEvent(new CustomEvent('ventas:slack:dm', {
                  detail: { prefillText: prefill },
                }));
              }}
              className="gap-2 text-chart-purple border-chart-purple/30 hover:bg-chart-purple/10 hover:text-chart-purple"
              data-testid="brief-share-slack"
            >
              <MessageSquare size={16} /> Share via Slack
            </Button>
            {canEdit && !editing && (
              <Button variant="outline" onClick={startEdit} className="gap-2" data-testid="edit-brief-btn">
                <Pencil size={16} /> Edit
              </Button>
            )}
            {canDelete && (
              <Button variant="outline" onClick={() => setShowDeleteConfirm(true)} className="gap-2 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" data-testid="delete-brief-btn">
                <Trash2 size={16} /> Delete
              </Button>
            )}
          </div>
        </div>

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <Card className="mb-6 border-red-300 bg-red-50">
            <CardContent className="p-5 flex items-center justify-between">
              <p className="font-medium text-red-800">Are you sure you want to delete this brief? This cannot be undone.</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} data-testid="cancel-delete">Cancel</Button>
                <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete">Delete</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* EDIT MODE */}
        {editing ? (
          <div className="space-y-6 mb-8">
            <Card className="glass">
              <CardHeader><CardTitle className="text-brand">Edit Campaign Details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Objective</Label>
                  <select value={editData.objective} onChange={e => setEditData({ ...editData, objective: e.target.value })} className="w-full mt-2 px-3 py-2 border border-input rounded-xl bg-white">
                    {['Leads','Sales','Traffic','Brand_Awareness','App_Installs'].map(o => <option key={o} value={o}>{o.replace('_',' ')}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Total Budget ($)</Label><Input type="number" value={editData.budget_total} onChange={e => setEditData({ ...editData, budget_total: e.target.value ? parseFloat(e.target.value) : '' })} className="mt-2" /></div>
                  <div><Label>Daily Budget ($)</Label><Input type="number" value={editData.budget_daily} onChange={e => setEditData({ ...editData, budget_daily: e.target.value ? parseFloat(e.target.value) : '' })} className="mt-2" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Start Date</Label><Input type="date" value={editData.start_date} onChange={e => setEditData({ ...editData, start_date: e.target.value })} className="mt-2" /></div>
                  <div><Label>End Date</Label><Input type="date" value={editData.end_date} onChange={e => setEditData({ ...editData, end_date: e.target.value })} className="mt-2" /></div>
                </div>
                <div>
                  <Label>Industry Vertical</Label>
                  <select value={editData.industry_vertical} onChange={e => setEditData({ ...editData, industry_vertical: e.target.value })} className="w-full mt-2 px-3 py-2 border border-input rounded-xl bg-white">
                    {['B2B_SaaS','Ecommerce','Healthcare','Finance','Travel','Education','Real_Estate'].map(v => <option key={v} value={v}>{v.replace('_',' ')}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Additional Notes</Label>
                  <textarea value={editData.additional_notes} onChange={e => setEditData({ ...editData, additional_notes: e.target.value })} className="w-full mt-2 px-3 py-2 border border-input rounded-xl bg-white min-h-[80px]" />
                </div>
              </CardContent>
            </Card>

            <Card className="glass">
              <CardHeader><CardTitle className="text-brand">Required Keywords</CardTitle></CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-3">
                  <Input placeholder="Add keyword..." value={keywordInput} onChange={e => setKeywordInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addEditKeyword())} />
                  <Button type="button" onClick={addEditKeyword}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(editData.mandatories?.required_keywords || []).map((kw, i) => (
                    <span key={`kw-${i}`} className="px-3 py-1 bg-brand/10 text-brand rounded-full text-sm flex items-center gap-1">
                      {kw} <button onClick={() => removeEditKeyword(i)} className="text-brand/50 hover:text-red-500 ml-1">&times;</button>
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="glass">
              <CardHeader><CardTitle className="text-brand">Landing Pages</CardTitle></CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-3">
                  <Input placeholder="https://..." value={landingPageInput} onChange={e => setLandingPageInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addEditLandingPage())} />
                  <Button type="button" onClick={addEditLandingPage}>Add</Button>
                </div>
                <div className="space-y-2">
                  {(editData.landing_pages || []).map((url, i) => (
                    <div key={`item-${i}`} className="flex items-center justify-between px-3 py-2 bg-white/50 rounded-lg text-sm">
                      <span className="truncate">{url}</span>
                      <button onClick={() => removeEditLandingPage(i)} className="text-text-tertiary hover:text-red-500 ml-2">&times;</button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button onClick={saveEdit} className="bg-brand hover:bg-brand-hover text-white" data-testid="save-edit-btn">Save Changes</Button>
              <Button variant="outline" onClick={() => setEditing(false)} data-testid="cancel-edit-btn">Cancel</Button>
            </div>
          </div>
        ) : (
          /* VIEW MODE */
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <Card className="glass">
                <CardHeader><CardTitle className="text-brand text-lg flex items-center gap-2"><Target size={18} /> Campaign Details</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <DetailRow label="Objective" value={brief.objective} />
                  <DetailRow label="Channel" value={CHANNEL_LABELS[brief.channel] || brief.channel} />
                  <DetailRow label="Industry" value={brief.industry_vertical?.replace('_', ' ')} />
                  <DetailRow label="Platforms" value={brief.platforms?.map(p => p.replace('_', ' ')).join(', ') || 'N/A'} />
                  <DetailRow label="Ad Products" value={brief.ad_products?.map(p => p.replace('_', ' ')).join(', ') || 'N/A'} />
                </CardContent>
              </Card>
              <Card className="glass">
                <CardHeader><CardTitle className="text-brand text-lg flex items-center gap-2"><DollarSign size={18} /> Budget & Timeline</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <DetailRow label="Total Budget" value={brief.budget_total ? `$${Number(brief.budget_total).toLocaleString()}` : 'N/A'} />
                  <DetailRow label="Daily Budget" value={brief.budget_daily ? `$${Number(brief.budget_daily).toLocaleString()}` : 'N/A'} />
                  <DetailRow label="Start Date" value={brief.start_date || 'N/A'} />
                  <DetailRow label="End Date" value={brief.end_date || 'N/A'} />
                </CardContent>
              </Card>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <Card className="glass">
                <CardHeader><CardTitle className="text-brand text-lg flex items-center gap-2"><Tag size={18} /> Required Keywords</CardTitle></CardHeader>
                <CardContent>
                  {brief.mandatories?.required_keywords?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">{brief.mandatories.required_keywords.map((kw, i) => (
                      <span key={`item-${i}`} className="px-3 py-1 bg-brand/10 text-brand rounded-full text-sm">{kw}</span>
                    ))}</div>
                  ) : <p className="text-text-tertiary text-sm">No required keywords specified</p>}
                </CardContent>
              </Card>
              <Card className="glass">
                <CardHeader><CardTitle className="text-brand text-lg flex items-center gap-2"><Globe size={18} /> Landing Pages</CardTitle></CardHeader>
                <CardContent>
                  {brief.landing_pages?.length > 0 ? (
                    <div className="space-y-2">{brief.landing_pages.map((url, i) => (
                      <div key={`item-${i}`} className="px-3 py-2 bg-white/50 rounded-lg text-sm text-brand truncate">{url}</div>
                    ))}</div>
                  ) : <p className="text-text-tertiary text-sm">No landing pages specified</p>}
                </CardContent>
              </Card>
            </div>
            {brief.additional_notes && (
              <Card className="glass mb-8">
                <CardHeader><CardTitle className="text-brand text-lg flex items-center gap-2"><FileText size={18} /> Notes</CardTitle></CardHeader>
                <CardContent><p className="text-text-secondary">{brief.additional_notes}</p></CardContent>
              </Card>
            )}
          </>
        )}

        {/* AI Generated Campaigns */}
        {!editing && brief.ai_generated_campaigns ? (
          <div className="space-y-6 mb-6">
            {/* Keyword Research Source Badge */}
            {brief.keywords_data?.semrush && (
              <div className="flex items-center gap-3 px-4 py-2.5 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg" data-testid="keyword-source-badge">
                <span className="px-2.5 py-1 bg-green-600 text-white text-xs font-bold rounded-full uppercase tracking-wide">SEMRush</span>
                <span className="text-sm text-green-800">
                  {brief.keywords_data.semrush.total_keywords || 0} real keywords researched
                  {brief.keywords_data.ai_expanded?.total_keywords ? ` + ${brief.keywords_data.ai_expanded.total_keywords} AI-expanded` : ''}
                </span>
              </div>
            )}
            {/* Action buttons */}
            <div className="flex gap-2 flex-wrap">
              <Button onClick={handleDownloadPdf} className="bg-bcm-orange hover:bg-bcm-orange/90 text-white gap-2" data-testid="download-pdf-btn">
                <Download size={16} /> Download PDF
              </Button>
              <Button onClick={handleSaveToDrive} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="save-to-drive-btn">
                <HardDrive size={16} /> {saving ? 'Saving...' : (brief.drive_files ? 'Re-save to Google Drive' : 'Save to Google Drive')}
              </Button>
              {(user.role === 'Admin' || user.role === 'Media_Buyer') && (
                <Button onClick={handlePushToSkai} className="bg-positive hover:bg-positive/80 text-white gap-2" data-testid="push-to-skai-btn">
                  Push to Skai
                </Button>
              )}
            </div>

            {/* Visual Campaign Tree */}
            <CampaignTree campaigns={brief.ai_generated_campaigns} />
          </div>
        ) : !editing ? (
          <Card className="glass mb-6">
            <CardContent className="p-8 text-center">
              {generating ? (
                <div className="space-y-4" data-testid="generation-progress">
                  {genProgress && (
                    <>
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-text-primary">{genProgress.detail}</span>
                        <span className="text-text-tertiary font-mono">{genProgress.percent}%</span>
                      </div>
                      <div className="h-2.5 bg-silver rounded-full overflow-hidden">
                        <div
                          className="h-full bg-brand rounded-full transition-all duration-700 ease-out"
                          style={{ width: `${genProgress.percent}%` }}
                          data-testid="generation-progress-bar"
                        />
                      </div>
                      <div className="flex items-center gap-2 text-xs text-text-tertiary">
                        <div className="animate-spin rounded-full h-3 w-3 border-b border-brand" />
                        <span>
                          {genProgress.phase === 'keyword_research' && 'Step 1/3 — Keyword research'}
                          {genProgress.phase === 'keyword_expansion' && 'Step 1/3 — AI keyword expansion (5 parallel calls)'}
                          {genProgress.phase === 'campaign_structure' && 'Step 2/3 — Building campaign structure'}
                          {genProgress.phase === 'saving' && 'Step 3/3 — Saving results'}
                          {genProgress.phase === 'starting' && 'Starting...'}
                          {genProgress.phase === 'resuming' && 'Reconnecting to generation in progress...'}
                          {genProgress.phase === 'done' && 'Done!'}
                        </span>
                      </div>
                    </>
                  )}
                  <p className="text-text-tertiary text-xs mt-2">You can navigate away — generation continues in the background.</p>
                </div>
              ) : (
                <>
                  <Sparkles size={40} className="mx-auto text-brand/30 mb-4" />
                  <h3 className="text-lg font-semibold text-text-primary mb-2">No campaigns generated yet</h3>
                  {brief.generation_error && (
                    <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="generation-error">
                      <p className="font-medium">Last generation attempt failed:</p>
                      <p className="text-red-600 mt-1">{brief.generation_error.includes('502') ? 'AI service temporarily unavailable. Please retry in a moment.' : brief.generation_error}</p>
                    </div>
                  )}
                  <p className="text-text-tertiary text-sm mb-6">Click below to use AI to generate a Skai-ready campaign structure.</p>
                  <Button onClick={handleGenerate} className="bg-brand hover:bg-brand-hover text-white gap-2" data-testid="generate-campaigns-btn">
                    <Sparkles size={18} /> {brief.generation_error ? 'Retry AI Campaign Generation' : 'Generate AI Campaign Structure'}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        ) : null}

        {/* Drive Files Info */}
        {!editing && brief.drive_files && (
          <Card className="glass mb-6">
            <CardHeader><CardTitle className="text-brand text-lg flex items-center gap-2"><HardDrive size={18} /> Saved to Google Drive</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {brief.drive_files.google_doc && (
                <a href={brief.drive_files.google_doc.web_view_link} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-2 text-brand hover:underline" data-testid="drive-doc-link">
                  <FileText size={16} /> {brief.drive_files.google_doc.file_name} <ExternalLink size={14} />
                </a>
              )}
              {brief.drive_files.skai_json && (
                <a href={brief.drive_files.skai_json.web_view_link} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-2 text-brand hover:underline" data-testid="drive-json-link">
                  <FileText size={16} /> {brief.drive_files.skai_json.file_name} <ExternalLink size={14} />
                </a>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

/* ─── Expandable Ad Copy Card ─── */
const formatDKI = (text) => {
  // {KeyWord:Some Default} → show "Some Default" with a subtle indicator
  return (text || '').replace(/\{KeyWord:([^}]+)\}/gi, (_, def) => `[${def.trim()}]`);
};

const AdPreview = ({ headlines, descriptions, finalUrl }) => {
  const visibleHeadlines = headlines.slice(0, 3).map(formatDKI);
  const extraHeadlines = headlines.slice(3).map(formatDKI);
  const allDescriptions = descriptions.map(formatDKI);
  const displayUrl = finalUrl ? finalUrl.replace(/^https?:\/\//, '').split('/')[0] : 'example.com';

  return (
    <div className="bg-white rounded-lg border border-border-subtle p-4 shadow-sm space-y-1" data-testid="ad-preview">
      <p className="text-[13px] text-text-tertiary">Sponsored</p>
      <p className="text-sm text-green-800 flex items-center gap-1">
        {displayUrl}
        <span className="text-text-tertiary text-xs">▾</span>
      </p>
      <p className="text-lg text-blue-700 font-medium leading-snug hover:underline cursor-default">
        {visibleHeadlines.join(' | ')}
      </p>
      {extraHeadlines.length > 0 && (
        <div className="pl-0 pt-0.5">
          {extraHeadlines.map((h, i) => (
            <span key={`extra-hl-${h}-${i}`} className="inline-block text-xs bg-blue-50 text-blue-600 border border-blue-100 rounded px-1.5 py-0.5 mr-1 mb-1 font-medium">
              {h}
            </span>
          ))}
        </div>
      )}
      {allDescriptions.map((d, i) => (
        <p key={`desc-${d.slice(0,20)}-${i}`} className="text-sm text-text-secondary leading-relaxed">{d}</p>
      ))}
    </div>
  );
};

const AdCopyCard = ({ ad }) => {
  const [expanded, setExpanded] = useState(false);
  const headlines = Array.isArray(ad.headlines) ? ad.headlines : (ad.headline ? [ad.headline] : []);
  const descriptions = Array.isArray(ad.descriptions) ? ad.descriptions : (ad.description ? [ad.description] : []);
  const finalUrl = ad.final_url || ad.url || '';

  return (
    <div data-testid="ad-copy-card">
      {ad.ad_type && <p className="text-xs text-text-tertiary mb-1.5 uppercase tracking-wide font-semibold">{ad.ad_type}</p>}

      {/* Collapsed: compact preview */}
      {!expanded && (
        <div
          onClick={() => setExpanded(true)}
          className="cursor-pointer group"
          data-testid="ad-copy-collapsed"
        >
          <AdPreview headlines={headlines.slice(0, 3)} descriptions={descriptions.slice(0, 1)} finalUrl={finalUrl} />
          {(headlines.length > 3 || descriptions.length > 1) && (
            <p className="text-xs text-blue-500 mt-1.5 group-hover:text-blue-700 transition-colors">
              Click to expand full ad ({headlines.length} headlines, {descriptions.length} descriptions)
            </p>
          )}
        </div>
      )}

      {/* Expanded: full ad preview */}
      {expanded && (
        <div>
          <AdPreview headlines={headlines} descriptions={descriptions} finalUrl={finalUrl} />
          {headlines.some(h => /\{KeyWord:/i.test(h)) && (
            <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
              [Bracketed text] = DKI placeholder (Dynamic Keyword Insertion defaults)
            </p>
          )}
          <button
            onClick={() => setExpanded(false)}
            className="text-xs text-text-tertiary mt-2 hover:text-text-primary transition-colors"
            data-testid="collapse-ad-copy-btn"
          >
            Collapse
          </button>
        </div>
      )}
    </div>
  );
};

/* ─── Visual Campaign Tree Component ─── */
const CampaignCard = ({ camp, ci }) => {
  const [open, setOpen] = useState(ci === 0);
  const campName = camp.campaign_name || camp.name || `Campaign ${ci + 1}`;
  const adGroups = camp.ad_groups || [];

  return (
    <Card className="border-2 border-border-subtle bg-white/80 overflow-hidden">
      {/* Campaign Header — click to toggle */}
      <div
        className="text-white p-4 cursor-pointer select-none"
        style={{ backgroundColor: '#1d585f' }}
        onClick={() => setOpen(!open)}
        data-testid={`campaign-header-${ci}`}
      >
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <FolderIcon size={20} /> {campName}
          </h3>
          <div className="flex items-center gap-2 flex-wrap text-xs">
            {camp.platform && <span className="px-2 py-1 bg-white/20 rounded">{camp.platform}</span>}
            {camp.campaign_type && <span className="px-2 py-1 bg-white/20 rounded">{camp.campaign_type}</span>}
            {camp.bid_strategy && <span className="px-2 py-1 bg-white/20 rounded">{camp.bid_strategy}</span>}
            {camp.daily_budget && <span className="px-2 py-1 bg-emerald-500/30 rounded">${camp.daily_budget}/day</span>}
            <span className="px-2 py-1 bg-white/10 rounded">
              {adGroups.length} ad group{adGroups.length !== 1 ? 's' : ''} · {open ? '▾' : '▸'}
            </span>
          </div>
        </div>
        {(camp.start_date || camp.end_date) && (
          <p className="text-white/60 text-xs mt-1">{camp.start_date} — {camp.end_date || 'Ongoing'}</p>
        )}
      </div>

      {/* Collapsible content */}
      {open && (
        <div className="p-4 space-y-4">
          {adGroups.map((ag, gi) => {
            const agName = ag.ad_group_name || ag.name || `Ad Group ${gi + 1}`;
            const keywords = ag.keywords || [];
            let ads = [];
            if (Array.isArray(ag.ads)) ads = ag.ads;
            else if (ag.ads) ads = [ag.ads];
            else if (Array.isArray(ag.ad_copy)) ads = ag.ad_copy;
            else if (ag.ad_copy) ads = [ag.ad_copy];
            else if (ag.responsive_search_ads) ads = ag.responsive_search_ads;
            if (ads.length === 0 && (ag.headlines || ag.descriptions)) {
              ads = [{ headlines: ag.headlines || [], descriptions: ag.descriptions || [], ad_type: 'RSA' }];
            }
            const negatives = ag.negative_keywords || ag.negatives || [];

            return (
              <div key={ag.ad_group_name || ag.name || `ag-${gi}`} className="border-l-4 border-blue-500 pl-4 ml-2">
                <h4 className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
                  <FolderIcon size={16} /> {agName}
                  {ag.default_bid && <span className="text-xs font-normal text-text-tertiary ml-2">Default Bid: ${ag.default_bid}</span>}
                </h4>

                {/* Keywords Table */}
                {keywords.length > 0 && (
                  <div className="mb-3 ml-4">
                    <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-1">Keywords</p>
                    <div className="bg-surface-page rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead><tr className="bg-surface-page text-left">
                          <th className="px-3 py-1.5 text-xs font-medium text-text-secondary">Keyword</th>
                          <th className="px-3 py-1.5 text-xs font-medium text-text-secondary">Match</th>
                          <th className="px-3 py-1.5 text-xs font-medium text-text-secondary">Bid</th>
                        </tr></thead>
                        <tbody>
                          {keywords.map((kw, ki) => {
                            const kwText = typeof kw === 'string' ? kw : (kw.keyword_text || kw.keyword || kw.text || JSON.stringify(kw));
                            const matchType = typeof kw === 'object' ? (kw.match_type || kw.matchType || '') : '';
                            const kwBid = typeof kw === 'object' ? (kw.max_cpc || kw.bid || kw.cpc || kw.estimated_cpc || kw.suggested_bid || '') : '';
                            return (
                              <tr key={`${kwText}-${matchType}-${ki}`} className="border-t border-border-subtle">
                                <td className="px-3 py-1.5 font-mono text-sm">{kwText}</td>
                                <td className="px-3 py-1.5"><MatchBadge type={matchType} /></td>
                                <td className="px-3 py-1.5 text-text-secondary">{kwBid ? `$${kwBid}` : ''}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Ad Copy */}
                {ads.length > 0 && (
                  <div className="mb-3 ml-4">
                    <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-1">Ad Copy</p>
                    <div className="space-y-2">
                      {ads.map((ad, ai) => (
                        <AdCopyCard key={`ad-${ad.ad_type || ''}-${ai}`} ad={ad} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Negative Keywords */}
                {negatives.length > 0 && (
                  <div className="mb-3 ml-4">
                    <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-1">Negative Keywords</p>
                    <div className="flex flex-wrap gap-1">
                      {negatives.map((nk, ni) => (
                        <span key={`neg-${ni}`} className="px-2 py-0.5 bg-red-50 text-red-600 text-xs rounded border border-red-100">
                          -{typeof nk === 'string' ? nk : (nk.keyword_text || nk.keyword || '')}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

const CampaignTree = ({ campaigns }) => {
  let skaiCampaigns = [];
  if (Array.isArray(campaigns)) skaiCampaigns = campaigns;
  else if (typeof campaigns === 'object') {
    skaiCampaigns = campaigns.skai_campaigns || campaigns.campaigns || [];
    if (!Array.isArray(skaiCampaigns)) skaiCampaigns = [skaiCampaigns];
  }

  const topNegatives = campaigns?.negative_keywords || [];
  const recommendations = campaigns?.recommendations || [];

  return (
    <div className="space-y-6" data-testid="campaign-tree">
      {skaiCampaigns.map((camp, ci) => (
        <CampaignCard key={camp.campaign_name || camp.name || `camp-${ci}`} camp={camp} ci={ci} />
      ))}

      {/* Top-level negatives */}
      {topNegatives.length > 0 && (
        <Card className="glass">
          <CardHeader><CardTitle className="text-red-600 text-lg">Account-Level Negative Keywords</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1">
              {topNegatives.map((neg, ni) => {
                const negText = typeof neg === 'string' ? neg : (neg.keyword_text || neg.keyword || JSON.stringify(neg));
                return <span key={`acct-neg-${negText}`} className="px-2 py-0.5 bg-red-50 text-red-600 text-xs rounded border border-red-200">{negText}</span>;
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <Card className="glass">
          <CardHeader><CardTitle className="text-brand text-lg flex items-center gap-2"><Sparkles size={18} /> AI Recommendations</CardTitle></CardHeader>
          <CardContent>
            <ul className="list-disc pl-5 space-y-1 text-sm text-text-secondary">
              {recommendations.map((rec, ri) => <li key={`rec-${ri}-${String(rec).slice(0,20)}`}>{typeof rec === 'string' ? rec : JSON.stringify(rec)}</li>)}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const MatchBadge = ({ type }) => {
  const colors = {
    EXACT: 'bg-purple-100 text-purple-700 border-purple-200',
    PHRASE: 'bg-blue-100 text-blue-700 border-blue-200',
    BROAD: 'bg-amber-100 text-amber-700 border-amber-200',
  };
  if (!type) return null;
  return <span className={`px-1.5 py-0.5 rounded text-xs border font-medium ${colors[type] || 'bg-gray-100 text-gray-600'}`}>{type}</span>;
};

const FolderIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);

const DetailRow = ({ label, value }) => (
  <div className="flex justify-between items-center">
    <span className="text-sm text-text-tertiary">{label}</span>
    <span className="text-sm font-medium text-text-primary">{value}</span>
  </div>
);

export default BriefDetail;
